/**
 * CSC custom-PDP add-to-cart helper.
 *
 * Replaces the per-PDP fetch + `window.location = '/cart'` pattern with:
 *   1. Disable + "Adding…" while POST is in flight
 *   2. POST /cart/add.js with optional bundle variant (from active delivery chip)
 *   3. On success: refresh Dawn cart-drawer + cart-icon-bubble via renderContents,
 *      open the drawer, restore the button label
 *   4. On failure: re-enable the button and show an inline red error below the form
 *
 * Wire-up (called from each PDP's {% javascript %} block):
 *   window.CscPdpCart && window.CscPdpCart.attach({
 *     root: document.querySelector('.<scope>-pdp'),
 *     form: '[data-<scope>-form]',
 *     deliveryGroup: '[data-<scope>-delivery]'   // optional
 *   });
 *
 * Chips can carry the bundle variant id as either `data-bundle-variant`
 * (singular) or `data-bundle-variants` (plural — legacy). Both supported.
 */
(function () {
  if (window.CscPdpCart) return;

  function getBundleVariantId(chip) {
    if (!chip) return null;
    return chip.getAttribute('data-bundle-variant') || chip.getAttribute('data-bundle-variants');
  }

  function getCartElement() {
    return document.querySelector('cart-drawer') || document.querySelector('cart-notification');
  }

  // Errors we are happy to show a customer: ones we wrote, plus Shopify's own
  // line-item messages ("You can only add 2 of that to your cart"). Anything
  // untagged is an internal fault and must NOT be printed under Add To Basket —
  // see the catch handler below.
  function userError(message) {
    var err = new Error(message);
    err.cscUserFacing = true;
    return err;
  }

  function ensureErrorEl(form) {
    var existing = form.parentNode.querySelector('.csc-cart-error');
    if (existing) return existing;
    var el = document.createElement('p');
    el.className = 'csc-cart-error';
    el.setAttribute('role', 'alert');
    el.style.cssText = [
      'display:none',
      'margin:12px 0 0',
      'padding:10px 14px',
      'background:#fff0f0',
      'border-left:3px solid var(--csc-red, #c8102e)',
      'color:var(--csc-red, #c8102e)',
      'font-family:var(--mono, monospace)',
      'font-size:12px',
      'line-height:1.45',
      'letter-spacing:0.04em'
    ].join(';');
    form.parentNode.insertBefore(el, form.nextSibling);
    return el;
  }

  function readQty(form) {
    // Dawn's standard template keeps the quantity input outside the <form> (linked by form="…"),
    // so fall back to the form-associated element list when nothing is found inside the form.
    var qtyInput = form.querySelector('input[name="quantity"]');
    if (!qtyInput && form.elements && typeof form.elements.namedItem === 'function') {
      qtyInput = form.elements.namedItem('quantity');
    }
    var n = qtyInput ? parseInt(qtyInput.value, 10) : 1;
    return isNaN(n) || n < 1 ? 1 : n;
  }

  function humanizePropertyKey(raw) {
    return raw
      .split('-')
      .filter(Boolean)
      .map(function (part) { return part.charAt(0).toUpperCase() + part.slice(1); })
      .join(' ');
  }

  function hasProperties(properties) {
    for (var key in properties) {
      if (Object.prototype.hasOwnProperty.call(properties, key)) return true;
    }
    return false;
  }

  function readFormProperties(form) {
    var properties = {};
    form.querySelectorAll('[name^="properties["]').forEach(function (input) {
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
      var match = input.name.match(/^properties\[(.+)\]$/);
      if (!match || !input.value) return;
      properties[match[1]] = input.value;
    });
    return properties;
  }

  function readCartAttributes(form) {
    var attributes = {};
    form.querySelectorAll('[name^="attributes["]').forEach(function (input) {
      if ((input.type === 'checkbox' || input.type === 'radio') && !input.checked) return;
      var match = input.name.match(/^attributes\[(.+)\]$/);
      if (!match || !input.value) return;
      attributes[match[1]] = input.value;
    });
    return attributes;
  }

  function readDataProperties(el, prefix) {
    var properties = {};
    if (!el || !el.attributes) return properties;
    Array.prototype.forEach.call(el.attributes, function (attr) {
      if (attr.name.indexOf(prefix) !== 0 || !attr.value) return;
      properties[humanizePropertyKey(attr.name.slice(prefix.length))] = attr.value;
    });
    return properties;
  }

  // Prompt 7 (19 Oct 2026): when the active delivery chip carries no data-line-property-*
  // attributes, record the customer's choice as a "Delivery" line-item property built only
  // from the chip's own visible text: label (lead-time element and any "+£…" price removed)
  // + " · " + lead time (the .plus-price element). A chip without a lead time gives the label alone.
  function deliveryLabelFromChip(chip) {
    if (!chip) return '';
    var clone = chip.cloneNode(true);
    var timeEl = clone.querySelector('.plus-price');
    var time = timeEl ? timeEl.textContent : '';
    if (timeEl && timeEl.parentNode) timeEl.parentNode.removeChild(timeEl);
    var label = clone.textContent.replace(/\+\s*£\s*[\d.,]+/g, '');
    label = label.replace(/\s+/g, ' ').replace(/^[\s·]+|[\s·]+$/g, '').trim();
    time = time.replace(/\s+/g, ' ').trim();
    if (!label) return '';
    return time ? label + ' \u00b7 ' + time : label;
  }

  function mergeProperties(target, source) {
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
    return target;
  }

  function setButtonState(btn, label) {
    if (!btn) return;
    btn.disabled = !!label;
    if (!btn._cscOriginal) btn._cscOriginal = (btn.querySelector('span') || btn).textContent.trim() || 'Add To Basket';
    var span = btn.querySelector('span');
    if (span) span.textContent = label || btn._cscOriginal;
    else btn.textContent = label || btn._cscOriginal;
  }

  // ── Prompt 13 (21 Oct 2026): "Delivery & Install" summary ──────────────────
  // Shared by every custom PDP that passes a deliveryGroup. While a chip whose
  // text carries a "+£…" amount (Back Garden Install, White Glove set-up) is
  // active:
  //   • the summary next to "Delivery & Install" (the .option-value in the same
  //     .option-group) reads "<chip label> · +£<amount>", using the amount
  //     printed on the chip;
  //   • a line "With <chip label>: £<tub + bundle>" is shown directly under the
  //     price (after .price-main) and the phone sticky bar shows the same total
  //     — but only when the live price of the chip's bundle variant
  //     (/variants/<id>.js) equals the amount printed on the chip, and the tub
  //     price of the variant in the form is known (Shopify's product meta, else
  //     /products/<handle>.js). If the two prices differ, or either is unknown,
  //     no total is shown anywhere — the prices must be real.
  // Any other chip restores the section's own summary text and the tub price.
  // Chips expose aria-pressed; the summary is announced politely.
  // Nothing in here touches the submit / add-to-basket flow further down.
  var bundlePriceCache = {};
  var productJsonPromise = null;

  function fetchJson(url) {
    return fetch(url, { headers: { 'Accept': 'application/json' }, credentials: 'same-origin' })
      .then(function (res) { return res.ok ? res.json() : null; })
      .catch(function () { return null; });
  }

  function chipTextWithoutLeadTime(chip) {
    var clone = chip.cloneNode(true);
    var timeEl = clone.querySelector('.plus-price');
    if (timeEl && timeEl.parentNode) timeEl.parentNode.removeChild(timeEl);
    return clone.textContent.replace(/\s+/g, ' ').trim();
  }

  // "+£799" / "+£1,000" exactly as printed on the chip (spaces removed); '' when the chip has no amount.
  function chipAmountText(chip) {
    var m = chipTextWithoutLeadTime(chip).match(/\+\s*£\s*([\d,]+(?:\.\d{1,2})?)/);
    return m ? '+£' + m[1] : '';
  }

  function chipAmountPence(chip) {
    var m = chipTextWithoutLeadTime(chip).match(/\+\s*£\s*([\d,]+(?:\.\d{1,2})?)/);
    if (!m) return null;
    var n = parseFloat(m[1].replace(/,/g, ''));
    return isNaN(n) ? null : Math.round(n * 100);
  }

  // The chip's label without its lead time and "+£…" amount, e.g. "Back Garden Install".
  function chipLabelOnly(chip) {
    return chipTextWithoutLeadTime(chip)
      .replace(/\+\s*£\s*[\d.,]+/g, '')
      .replace(/\s+/g, ' ')
      .replace(/^[\s·]+|[\s·]+$/g, '')
      .trim();
  }

  // Same shape as Liquid's money_without_trailing_zeros: £4,495 / £1,533.99
  function formatMoney(pence) {
    var pounds = pence / 100;
    var s = pounds % 1 === 0 ? pounds.toFixed(0) : pounds.toFixed(2);
    return '£' + s.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function variantPriceFromMeta(variantId) {
    try {
      var meta = window.ShopifyAnalytics && window.ShopifyAnalytics.meta;
      var variants = meta && meta.product && meta.product.variants;
      if (!variants) return null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId) && typeof variants[i].price === 'number') return variants[i].price;
      }
    } catch (e) {}
    return null;
  }

  function tubPrice(variantId) {
    var fromMeta = variantPriceFromMeta(variantId);
    if (fromMeta != null) return Promise.resolve(fromMeta);
    if (!productJsonPromise) {
      var m = window.location.pathname.match(/\/products\/([^\/?#]+)/);
      productJsonPromise = m ? fetchJson('/products/' + m[1] + '.js') : Promise.resolve(null);
    }
    return productJsonPromise.then(function (product) {
      var variants = product && product.variants;
      if (!variants) return null;
      for (var i = 0; i < variants.length; i++) {
        if (String(variants[i].id) === String(variantId) && typeof variants[i].price === 'number') return variants[i].price;
      }
      return null;
    });
  }

  function bundlePrice(variantId) {
    if (!bundlePriceCache[variantId]) {
      bundlePriceCache[variantId] = fetchJson('/variants/' + encodeURIComponent(variantId) + '.js').then(function (v) {
        return v && typeof v.price === 'number' ? v.price : null;
      });
    }
    return bundlePriceCache[variantId];
  }

  function setupDeliverySummary(root, form, deliveryGroup) {
    if (!root || !form || !deliveryGroup || deliveryGroup._cscSummaryBound) return;
    var chips = deliveryGroup.querySelectorAll('.chip');
    if (!chips.length) return;
    deliveryGroup._cscSummaryBound = true;

    var optionGroup = deliveryGroup.closest ? deliveryGroup.closest('.option-group') : null;
    var summary = optionGroup ? optionGroup.querySelector('.option-value') : null;
    if (!summary && deliveryGroup.previousElementSibling) summary = deliveryGroup.previousElementSibling.querySelector('.option-value');
    var priceMain = root.querySelector('.price-main');
    var stickyPrice = root.querySelector('.csc-sticky-buy__price') || document.querySelector('.csc-sticky-buy__price');

    var totalEl = null;
    if (priceMain && priceMain.parentNode) {
      totalEl = document.createElement('div');
      totalEl.className = 'csc-pdp-install-total';
      totalEl.setAttribute('data-csc-install-total', '');
      totalEl.hidden = true;
      priceMain.parentNode.insertBefore(totalEl, priceMain.nextSibling);
    }
    var summaryDefault = summary ? summary.textContent : '';
    var stickyDefault = stickyPrice ? stickyPrice.textContent : '';
    if (summary) {
      summary.setAttribute('aria-live', 'polite');
      summary.setAttribute('aria-atomic', 'true');
    }
    // What the helper found on this page (read by QA, no visual effect).
    deliveryGroup.setAttribute('data-csc-summary', summary ? 'bound' : 'missing');
    deliveryGroup.setAttribute('data-csc-total-line', totalEl ? 'bound' : 'missing');
    deliveryGroup.setAttribute('data-csc-sticky-price', stickyPrice ? 'bound' : 'missing');

    // Warm the bundle price cache so the total appears as soon as a chip is chosen.
    Array.prototype.forEach.call(chips, function (chip) {
      var id = getBundleVariantId(chip);
      if (id) bundlePrice(id);
    });

    var seq = 0;
    function clearTotal() {
      if (totalEl) { totalEl.hidden = true; totalEl.textContent = ''; }
      if (stickyPrice) stickyPrice.textContent = stickyDefault;
    }
    function update() {
      var active = deliveryGroup.querySelector('.chip.active');
      Array.prototype.forEach.call(deliveryGroup.querySelectorAll('.chip'), function (chip) {
        chip.setAttribute('aria-pressed', chip === active ? 'true' : 'false');
      });
      var run = ++seq;
      var amountText = active ? chipAmountText(active) : '';
      var label = active ? chipLabelOnly(active) : '';
      if (!active || !amountText || !label) {
        if (summary) summary.textContent = summaryDefault;
        clearTotal();
        return;
      }
      if (summary) summary.textContent = label + ' · ' + amountText;
      var bundleId = getBundleVariantId(active);
      var amount = chipAmountPence(active);
      var idInput = form.querySelector('input[name="id"]');
      if (!bundleId || amount == null || !idInput) { clearTotal(); return; }
      Promise.all([tubPrice(idInput.value), bundlePrice(bundleId)]).then(function (prices) {
        if (run !== seq) return; // a later selection has superseded this one
        var tub = prices[0], bundle = prices[1];
        active.setAttribute('data-csc-price-check', bundle == null ? 'unknown' : (bundle === amount ? 'match' : 'mismatch'));
        if (tub == null || bundle == null || bundle !== amount) { clearTotal(); return; }
        var total = formatMoney(tub + bundle);
        if (totalEl) { totalEl.textContent = 'With ' + label + ': ' + total; totalEl.hidden = false; }
        if (stickyPrice) stickyPrice.textContent = total;
      });
    }
    var queued = false;
    function schedule() {
      if (queued) return;
      queued = true;
      Promise.resolve().then(function () { queued = false; update(); });
    }
    if (window.MutationObserver) {
      // Each section's own chip script toggles .active; react to that whatever the listener order.
      new MutationObserver(schedule).observe(deliveryGroup, { attributes: true, attributeFilter: ['class'], subtree: true });
    } else {
      deliveryGroup.addEventListener('click', function () { setTimeout(update, 0); });
    }
    form.addEventListener('change', schedule); // a variant change can change the tub price
    update();
  }

  function attach(opts) {
    if (!opts || !opts.root) return;
    var form = typeof opts.form === 'string' ? opts.root.querySelector(opts.form) : opts.form;
    if (!form) return;
    if (form._cscBound) return;
    form._cscBound = true;

    var deliveryGroup = null;
    if (opts.deliveryGroup) {
      deliveryGroup = typeof opts.deliveryGroup === 'string' ? opts.root.querySelector(opts.deliveryGroup) : opts.deliveryGroup;
    }
    var submitBtn = form.querySelector('button[type="submit"]');
    var errorEl = ensureErrorEl(form);

    // Prompt 13 (21 Oct 2026): Delivery & Install summary, total line and sticky-bar total (see setupDeliverySummary above).
    setupDeliverySummary(opts.root, form, deliveryGroup);

    // Bottom CTA-banner "Add To Basket" buttons (and any other element marked
     // with [data-csc-trigger-submit] inside `root`) trigger the same submit
     // flow as the main form's primary button. Cleaner than anchor-to-#top
     // and means a second add-to-cart point further down the PDP actually
     // adds to cart instead of just scrolling up.
    opts.root.querySelectorAll('[data-csc-trigger-submit]').forEach(function (trigger) {
      if (trigger._cscTriggerBound) return;
      trigger._cscTriggerBound = true;
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        if (typeof form.requestSubmit === 'function') {
          form.requestSubmit();
        } else {
          form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
        }
      });
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitBtn && submitBtn.disabled) return;
      errorEl.style.display = 'none';
      errorEl.textContent = '';

      // Build the items list — main product + optional bundle variant from active chip
      var idInput = form.querySelector('input[name="id"]');
      if (!idInput) return;
      var activeChip = deliveryGroup ? deliveryGroup.querySelector('.chip.active') : null;
      var chipProperties = readDataProperties(activeChip, 'data-line-property-');
      if (activeChip && !hasProperties(chipProperties)) {
        var deliveryLabel = deliveryLabelFromChip(activeChip);
        if (deliveryLabel) chipProperties = { Delivery: deliveryLabel };
      }
      var mainProperties = mergeProperties(readFormProperties(form), chipProperties);
      var mainItem = { id: parseInt(idInput.value, 10), quantity: readQty(form) };
      if (hasProperties(mainProperties)) mainItem.properties = mainProperties;
      var items = [mainItem];

      if (deliveryGroup) {
        var bundleId = getBundleVariantId(activeChip);
        if (bundleId) {
          var bundleItem = { id: parseInt(bundleId, 10), quantity: 1 };
          var bundleProperties = readDataProperties(activeChip, 'data-bundle-property-');
          if (hasProperties(bundleProperties)) bundleItem.properties = bundleProperties;
          items.push(bundleItem);
        }
      }

      var cartEl = getCartElement();
      var sections = null;
      if (cartEl && typeof cartEl.getSectionsToRender === 'function') {
        sections = cartEl.getSectionsToRender().map(function (s) { return s.id; }).join(',');
      }

      var cartAttributes = readCartAttributes(form);
      var body = { items: items };
      if (sections) {
        body.sections = sections;
        body.sections_url = window.location.pathname;
      }

      setButtonState(submitBtn, 'Adding…');

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      })
        .then(function (res) {
          return res.text().then(function (txt) {
            // /cart/add.js returns Content-Type: text/javascript (Shopify convention
            // because the URL ends in .js), even though the body is JSON. So we can't
            // pre-filter on content-type — trust JSON.parse as the source of truth.
            // Truly-non-JSON responses (Cloudflare HTML challenge, login wall) will
            // throw here and get the same friendly error.
            try {
              var data = JSON.parse(txt);
              return { ok: res.ok, data: data };
            } catch (e) {
              throw userError('We couldn’t reach the basket right now. Please try again, or refresh the page if the issue persists.');
            }
          });
        })
        .then(function (r) {
          if (!r.ok) {
            var msg = (r.data && (r.data.description || r.data.message)) || 'Sorry — something went wrong. Please try again.';
            throw userError(msg);
          }
          if (hasProperties(cartAttributes)) {
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ attributes: cartAttributes })
            }).then(function (attrRes) {
              if (!attrRes.ok) throw userError('Pre-order details could not be saved to the basket. Please try again.');
              return r;
            });
          }
          return r;
        })
        .then(function (r) {

          // Refresh the cart UI in place
          if (cartEl && typeof cartEl.renderContents === 'function') {
            if (typeof cartEl.setActiveElement === 'function') {
              cartEl.setActiveElement(document.activeElement);
            }
            cartEl.renderContents(r.data); // also opens the drawer
          } else {
            // No Dawn cart element on this page — fall back to redirecting
            window.location.href = '/cart';
            return;
          }

          // Publish Dawn's pub-sub event so other components (mini-cart, etc.) refresh
          if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: 'csc-pdp-cart', productVariantId: idInput.value });
          }

          // Screen-reader announcement (Goal C)
          if (window.cscCartFeedback) {
            window.cscCartFeedback.announce(window.cscCartFeedback.titleFromResponse(r.data));
          }

          setButtonState(submitBtn, null); // restore original label
        })
        .catch(function (err) {
          setButtonState(submitBtn, null);
          // Never print an internal fault to a customer. Before this guard, a
          // TypeError thrown inside the cart drawer surfaced here verbatim as
          // "null is not an object (evaluating 'parsedState.sections[section.id]')"
          // in the red box under Add To Basket — the item WAS in the basket, but
          // the customer read it as a failed order and left.
          if (err && err.cscUserFacing && err.message) {
            errorEl.textContent = err.message;
          } else {
            if (window.console && console.error) console.error('[csc-pdp-cart]', err);
            errorEl.textContent = 'Sorry — we couldn’t add that to your basket. Please try again, or call 01293 824 094.';
          }
          errorEl.style.display = 'block';
        });
    });
  }

  window.CscPdpCart = { attach: attach };
})();
